"use client";

import { Calendar, ChevronRight, Handshake, Users } from "lucide-react";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { mediaUrlFromAsset } from "@/lib/media";
import type { MediaAsset } from "@/lib/media/types";
import { cn } from "@/lib/utils";

interface HostedByCardProps {
  hiive: {
    name: string;
    logoImage: MediaAsset | null;
    slug: string;
  };
  eventCount: number;
  memberCount: number;
  className?: string;
}

export function HostedByCard({
  hiive,
  eventCount,
  memberCount,
  className,
}: HostedByCardProps) {
  return (
    <div className={cn("space-y-3", className)}>
      <h3 className="text-xl font-bold text-text-primary dark:text-white">
        Hosted by
      </h3>
      <BackAwareLink
        href={`/hiives/${hiive.slug}`}
        // Empty prefetch, repeated per list row — see back-aware-link.tsx.
        prefetch={false}
        className="grid grid-cols-2 rounded-2xl border border-border bg-surface p-4 shadow-sm transition-colors hover:bg-surface-elevated dark:border-white/[0.12] dark:bg-black/55 dark:shadow-none dark:hover:bg-black/65"
      >
        <div className="flex flex-col items-center justify-center gap-2 border-r border-border pr-4 dark:border-white/[0.1]">
          {hiive.logoImage ? (
            <Image
              src={mediaUrlFromAsset(hiive.logoImage) ?? ""}
              alt={hiive.name}
              width={64}
              height={64}
              className="h-16 w-16 rounded-xl object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-surface-alt dark:bg-glass-bg-08">
              <Users className="h-7 w-7 text-text-secondary dark:text-white/50" />
            </div>
          )}
          <p className="max-w-full truncate text-center text-sm font-medium text-text-primary dark:text-white">
            {hiive.name}
          </p>
        </div>
        <div className="flex flex-col justify-center gap-3 pl-4">
          <div className="flex items-center gap-3">
            <Calendar className="h-4 w-4 text-text-tertiary dark:text-white/50" />
            <span className="text-sm font-semibold text-text-primary dark:text-white">
              {eventCount}
            </span>
            <span className="text-xs text-text-tertiary dark:text-white/50">
              Events
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Users className="h-4 w-4 text-text-tertiary dark:text-white/50" />
            <span className="text-sm font-semibold text-text-primary dark:text-white">
              {memberCount}
            </span>
            <span className="text-xs text-text-tertiary dark:text-white/50">
              Members
            </span>
          </div>
        </div>
      </BackAwareLink>
    </div>
  );
}

/**
 * "In collaboration with" under the host card — one row per accepted partner,
 * each opening that Hiive, so the event is a doorway into every community
 * that put it on (and not only the one that owns the listing).
 */
export function CollaboratorsCard({
  collaborators,
  className,
}: {
  collaborators: Array<{
    id: number;
    name: string;
    slug: string;
    logoImage: unknown;
  }>;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      <h3 className="flex items-center gap-2 text-xl font-bold text-text-primary dark:text-white">
        In collaboration with
        <Handshake
          className="size-5 text-text-tertiary dark:text-white/50"
          aria-hidden="true"
        />
      </h3>
      <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-surface shadow-sm dark:divide-white/[0.08] dark:border-white/[0.12] dark:bg-black/55 dark:shadow-none">
        {collaborators.map((partner) => {
          const src = mediaUrlFromAsset(
            partner.logoImage as Parameters<typeof mediaUrlFromAsset>[0],
          );
          return (
            <li key={partner.id}>
              <BackAwareLink
                href={`/hiives/${partner.slug}`}
                prefetch={false}
                className="flex items-center gap-3 p-3 transition-colors hover:bg-surface-elevated dark:hover:bg-black/65"
              >
                {src ? (
                  <Image
                    src={src}
                    alt=""
                    width={44}
                    height={44}
                    className="size-11 rounded-xl object-cover"
                  />
                ) : (
                  <span className="flex size-11 items-center justify-center rounded-xl bg-surface-alt dark:bg-glass-bg-08">
                    <Users className="size-5 text-text-secondary dark:text-white/50" />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-text-primary dark:text-white">
                    {partner.name}
                  </span>
                  <span className="block text-xs text-text-tertiary dark:text-white/50">
                    Partner Hiive
                  </span>
                </span>
                <ChevronRight
                  className="size-4 shrink-0 text-text-tertiary dark:text-white/40"
                  aria-hidden="true"
                />
              </BackAwareLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
